import React from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import { Checkbox, TableBody, TableCell, TableRow } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { ChapterIndicatorThemeDto, IndicatorDto, IndicatorValue } from '../../../types/indicator';
import DangerIconButton from '../../../styled/DangerIconButton';
import IndicatorSlider from './IndicatorSlider';
import useStore from '../../../store/storeContext';

interface Props {
    indicatorList: IndicatorDto[];
    indicatorPackageTheme?: ChapterIndicatorThemeDto | null;
    updateIndicatorList: () => void;
}

function EditIndicatorListBody(props: Props) {
    const context = useStore();
    const { snackbar, document, dialog } = context;

    async function setEnabled(indicator: IndicatorDto, enabled: boolean): Promise<void> {
        const { indicatorPackageTheme } = props;
        if (!indicatorPackageTheme) return;

        try {
            // Change value before request, so it doesn't look like it's lagging.
            runInAction(() => (indicator.enabled = enabled));
            await context.indicator.enableIndicator(indicator);
            props.updateIndicatorList();
        } catch (e) {
            // If request fails, change value back to what it was
            runInAction(() => (indicator.enabled = !enabled));
            snackbar.showError(e.data?.code);
        }
    }

    async function setPrintable(indicator: IndicatorDto, printable: boolean): Promise<void> {
        const { indicatorPackageTheme } = props;
        if (!indicatorPackageTheme) return;

        try {
            // Change value before request, so it doesn't look like it's lagging.
            runInAction(() => (indicator.printable = printable));
            await context.indicator.setPrintable(indicator);
        } catch (e) {
            // If request fails, change value back to what it was
            runInAction(() => (indicator.printable = !printable));
            snackbar.showError(e.data?.code);
        }
    }

    async function handleTopSelection(indicator: IndicatorDto, selected: boolean): Promise<void> {
        try {
            if (selected) {
                await context.indicator.addIndicatorTop(indicator);
            } else {
                await context.indicator.removeIndicatorTop(indicator);
            }
        } catch (e) {
            snackbar.showError(e.data?.code);
        }
    }

    async function remove(indicator: IndicatorDto): Promise<void> {
        const { translate } = context.localization;

        const confirmText = translate('CONFIRM_INDICATOR_REMOVAL_SUBTITLE') + ': ' + indicator.name + '?';
        const confirmation = await dialog.getConfirmation(undefined, confirmText);
        if (!confirmation) return;

        try {
            await context.indicator.removeIndicator(indicator);
            snackbar.showSuccess();
        } catch (e) {
            snackbar.showError(e.data?.code);
        }
    }

    function indicatorTypeText(indicator: IndicatorDto): string {
        const { translate } = context.localization;

        switch (indicator.type) {
            case 'P':
                return translate('BASE_INDICATOR');
            case 'T':
                return translate('FILL_INDICATOR');
            case 'L':
                return translate('CUSTOM_INDICATOR');
            case 'M':
                return translate('OTHER_INDICATOR');
            default:
                return '';
        }
    }

    function checkboxType(indicator: IndicatorDto): 'none' | 'normal' | 'checked' {
        const { hyteIdsKLossi, hyteIdsALossi } = context.session;
        const { isRegional } = document.currentDocument.organization;

        if (indicator.textOnly) return 'none';
        else if (indicator.sourceCode === 'CHP_CUSTOM') return 'none';
        else if (!isRegional && indicator.sotkanetId && hyteIdsKLossi.includes(indicator.sotkanetId.toString()))
            return 'checked';
        else if (isRegional && indicator.sotkanetId && hyteIdsALossi.includes(indicator.sotkanetId.toString()))
            return 'checked';
        else return 'normal';
    }

    async function setIndicatorValue(indicator: IndicatorDto, direction: IndicatorValue): Promise<void> {
        const { indicatorPackageTheme } = props;
        if (!indicatorPackageTheme) return;

        const directionBeforeUpdate = indicator.direction;

        try {
            // Change value before request, so it doesn't look like it's lagging.
            runInAction(() => (indicator.direction = direction));
            await context.indicator.saveIndicatorDirection(indicator);
        } catch (e) {
            // If request fails, change value back to what it was
            runInAction(() => (indicator.direction = directionBeforeUpdate));
            snackbar.showError(e.data?.code);
        }
    }

    return (
        <TableBody>
            {props.indicatorList.map((indicator) => (
                <TableRow key={indicator.id}>
                    <TableCell padding="checkbox" style={{ width: '9%' }}>
                        <Checkbox
                            checked={indicator.enabled || false}
                            onChange={(event): Promise<void> => setEnabled(indicator, event.target.checked)}
                        />
                    </TableCell>
                    <TableCell style={{ width: '41%' }}>{indicator?.name}</TableCell>
                    <TableCell>{indicatorTypeText(indicator)}</TableCell>
                    <TableCell>
                        <IndicatorSlider
                            value={indicator.direction !== null ? indicator.direction : 1}
                            onChange={(newValue): Promise<void> => setIndicatorValue(indicator, newValue)}
                        />
                    </TableCell>
                    <TableCell padding="checkbox" align="right">
                        <Checkbox
                            checked={indicator.printable || false}
                            onChange={(event): Promise<void> => setPrintable(indicator, event.target.checked)}
                        />
                    </TableCell>
                    {!document.isAnotherReport && document.hasSummary && (
                        <TableCell padding="checkbox" align="right">
                            {checkboxType(indicator) === 'none' ? null : checkboxType(indicator) === 'checked' ? (
                                <Checkbox checked={true} />
                            ) : (
                                <Checkbox
                                    checked={context.indicator.indicatorTops.some((top) => {
                                        return (
                                            top.indicatorId === indicator.indicatorSourceId &&
                                            props.indicatorPackageTheme?.id === top.themeId
                                        );
                                    })}
                                    onChange={(event): Promise<void> =>
                                        handleTopSelection(indicator, event.target.checked)
                                    }
                                />
                            )}
                        </TableCell>
                    )}
                    <TableCell padding="checkbox" align="right">
                        {indicator.removable && (
                            <DangerIconButton onClick={(): Promise<void> => remove(indicator)}>
                                <Delete />
                            </DangerIconButton>
                        )}
                    </TableCell>
                </TableRow>
            ))}
        </TableBody>
    );
}

export default observer(EditIndicatorListBody);
